if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Inject override styles after the product HTML is loaded
            // to ensure they take precedence over inline <style> blocks
            // from the fetched product page HTML
            this.injectQuickAddOverrides();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      injectQuickAddOverrides() {
        const styleId = 'quick-add-override-styles';
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          /* ===== Quick-Add Modal Override Styles ===== */
          /* These override inline styles from the fetched product page HTML.
             Injected via JS to ensure they load AFTER the inline <style> blocks. */

          /* Fix 1: Product title - ensure text is visible in the grey box */
          quick-add-modal .product__title > h1 {
            display: block !important;
            font-size: calc(var(--font-heading-scale) * 2.4rem) !important;
            font-weight: 700 !important;
            line-height: 1.3 !important;
            margin: 0 !important;
            color: rgb(var(--color-foreground)) !important;
          }

          quick-add-modal .product__title {
            padding: 1.5rem 2rem !important;
            border-radius: 1.6rem !important;
            background: rgba(var(--color-foreground), 0.03) !important;
            border: 0.1rem solid rgba(var(--color-foreground), 0.08) !important;
          }

          /* The outer desktop panel owns scrolling, allowing the section menu
             to remain sticky while customers move through product details. */
          @media screen and (min-width: 750px) {
            quick-add-modal .quick-add-modal__content {
              overflow-x: hidden !important;
            }
            quick-add-modal .quick-add-modal__content-info {
              overflow: visible !important;
            }
          }

          /* Fix 2: Keep every purchase action the same width. */
          quick-add-modal :is(
            .product-form__buttons,
            .product-form__enquiry-wrap,
            .mtf-button-wrapper,
            .nationwide-button-wrapper,
            .comparison-entry-link
          ) {
            box-sizing: border-box !important;
            width: 100% !important;
            max-width: 44rem !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }

          quick-add-modal .product-buy-buttons-block > .button,
          quick-add-modal .product-form__enquiry-wrap .button {
            display: flex !important;
            width: 100% !important;
            max-width: 44rem !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }

          /* Fix 3: Payment icon modals - positioned near the payment icons row
             On desktop: right-aligned at the vertical position of the icons
             On mobile: centered horizontally at the vertical position of the icons */
          quick-add-modal .product__payment-icons-row {
            position: relative !important;
          }

          /* On mobile only, shrink icons and tighten gap so all 5 fit in one row */
          @media screen and (max-width: 749px) {
            quick-add-modal .product__payment-icons-row {
              gap: 0 !important;
            }
            quick-add-modal .product__payment-icon {
              width: 50px !important;
            }
          }

          quick-add-modal .product__payment-modal {
            position: absolute !important;
            z-index: 9999 !important;
            pointer-events: none !important;
            /* Position below the payment icons row */
            top: 100% !important;
            left: 0 !important;
            right: 0 !important;
            height: auto !important;
          }

          quick-add-modal .product__payment-modal-overlay {
            display: none !important;
          }

          quick-add-modal .product__payment-modal-dialog {
            position: relative !important;
            z-index: 10000 !important;
            width: min(92vw, 50rem) !important;
            max-height: 70vh !important;
            overflow: auto !important;
            background: #f0f0f0 !important;
            border: 0.1rem solid #d8d8d8 !important;
            color: rgb(var(--color-foreground)) !important;
            border-radius: 1.2rem !important;
            box-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.15) !important;
            padding: 2.4rem !important;
            pointer-events: auto !important;
          }

          @media screen and (min-width: 750px) {
            quick-add-modal .product__payment-modal-dialog {
              margin-left: auto !important;
              margin-right: 0 !important;
            }
          }

          @media screen and (max-width: 749px) {
            quick-add-modal .product__payment-modal-dialog {
              margin-left: auto !important;
              margin-right: auto !important;
            }
          }

          @media screen and (min-width: 990px) {
            quick-add-modal .product__payment-modal-dialog {
              max-height: 80vh !important;
            }
          }

          /* Fix 4: Add bottom padding on mobile so there's white space
             between the last accordion and the modal bottom */
          @media screen and (max-width: 749px) {
            quick-add-modal .product__accordion-below-media {
              margin-bottom: 2rem !important;
              padding-bottom: 1rem !important;
            }
          }

          /* Fix 5: Responsive embedded videos (iframes) inside accordion content.
             On desktop: span the full accordion width.
             On mobile: constrained to accordion width, not overflowing. */
          quick-add-modal .product__accordion-content iframe {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            aspect-ratio: 16 / 9 !important;
          }

          /* Fix 6: Style the quick-add modal close button to match the green/indigo
             close buttons used in the payment icon modals on the product page. */
          quick-add-modal .quick-add-modal__toggle {
            background: #5DFD8B !important;
            color: #0B0B50 !important;
            border: none !important;
          }
          quick-add-modal .quick-add-modal__toggle:hover {
            opacity: 0.8 !important;
          }
        `;
        document.head.appendChild(style);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
